import type { AnyFunction } from '@seen/core/utils'
import { type DebouncedFunc, debounce } from 'lodash-es'
import { useMemo, useRef } from 'react'

// Contract, in two halves — they pull in opposite directions, so keep them apart.
//
// The WRITE TARGET — which record, which route, which id this call acts on — must be passed as an
// argument, not read from the closure. lodash captures arguments when a call is scheduled, while the
// closure is re-read when it fires, so a closed-over target resolves to whatever it became by then:
// the save lands on whatever row the user navigated to during the wait.
//
// The PAYLOAD may go either way, and callers that snapshot a value at call time (search text, a
// picked option) are right to pass it as an argument. But a caller that debounces a LIVE document
// behind a gate should read the payload from a ref inside the callback, so the one call that
// survives carries the document as it stands when it FIRES. lodash's trailing call does use the most
// recent call's arguments, so an eager payload is only stale for the window between the last call
// and the fire — but that window is the whole point of a 2.5s debounce, and it is also exactly the
// window in which a gate that declined to re-queue (a change no branch claimed) contributes no later
// call to refresh the argument. Reading per-fire state removes the dependency on how many times the
// gate happened to fire.
export function useDebouncedCallback<TCallback extends AnyFunction>(
  callback: TCallback,
  wait: number,
  maxWait?: number
): DebouncedFunc<TCallback> {
  const callbackRef = useRef(callback)
  callbackRef.current = callback

  // `debounce` creates a debounced function that delays invoking func until after wait milliseconds have elapsed since the last time the debounced function was invoked. As such, its internals matter and we need to memoize it to avoid recreating it on every render.
  const debounced = useMemo(() => {
    const wrappedFn = (...args: Parameters<TCallback>): ReturnType<TCallback> =>
      callbackRef.current(...args) // We use a wrapper around `callbackRef.current` to ensure that the function debounce calls is always the latest version.
    return debounce(wrappedFn, wait, {
      maxWait: maxWait ?? wait * 5,
    })
  }, [wait, maxWait])

  return debounced
}
